import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Star, BookOpen, Leaf, Heart, ShieldCheck } from 'lucide-react';
import { UrgencyBar } from '../components/UrgencyBar';
import { SocialProofPopup } from '../components/SocialProofPopup';
// Image URLs
const IMAGES = {
  main: 'https://cdn.magicpatterns.com/uploads/jj5gssJYBwAzmpJ92DT9Sv/ChatGPT_Image_2_ene_2026,_02_30_48.png',
  bonus1: 'https://cdn.magicpatterns.com/uploads/rqT8aZJhPmvLyXjjnW2kxL/ChatGPT_Image_2_ene_2026,_03_22_45.png',
  bonus2: 'https://cdn.magicpatterns.com/uploads/qUaDeWyo7tgaLComgZ2n9G/ChatGPT_Image_2_ene_2026,_03_34_47.png',
  bonus3: 'https://cdn.magicpatterns.com/uploads/ehwiWL6dUit9aAJveqjoLm/ChatGPT_Image_2_ene_2026,_03_36_59.png'
};
const CHECKOUT_URL = 'https://checkout.rosetadigital.com/cart/46059308187801:1';
const TESTIMONIALS = [{
  name: 'Claudia Martínez',
  age: 42,
  city: 'Córdoba',
  text: 'Siempre quise usar plantas pero me daba miedo equivocarme. Esta guía me dio la seguridad que necesitaba. Es muy clara y fácil de seguir.'
}, {
  name: 'Mariana Rossi',
  age: 38,
  city: 'Buenos Aires',
  text: 'Lo que más me gusta es que no da vueltas. Voy directo a lo que necesito. Ya preparé mi primer botiquín natural y me siento feliz.'
}, {
  name: 'Silvia Gómez',
  age: 51,
  city: 'Rosario',
  text: 'Me sentía abrumada con tanta información en internet. Este ebook ordenó todo. Es mi libro de cabecera ahora.'
}, {
  name: 'Fernanda López',
  age: 45,
  city: 'Mendoza',
  text: 'Los bonos son excelentes. Las fichas prácticas me salvaron más de una vez. Vale cada peso, es una inversión en salud.'
}, {
  name: 'Beatriz Quiroga',
  age: 55,
  city: 'La Plata',
  text: 'Lenguaje simple y amoroso. Se nota que está hecho pensando en nosotras. Logré crear el hábito de mis tecitos diarios.'
}];
export function HerbolariaLanding() {
  const scrollToPurchase = () => {
    const element = document.getElementById('purchase-section');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const fadeInUp = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  return <div className="min-h-screen bg-[#FDFBF7] font-sans text-gray-800 overflow-x-hidden" data-id="element-15">
      <UrgencyBar data-id="element-16" />
      <SocialProofPopup data-id="element-17" />

      {/* Hero Section */}
      <header className="relative pt-12 pb-20 px-4 md:pt-20 md:pb-32 overflow-hidden" data-id="element-18">
        <div className="absolute top-0 left-0 w-full h-full bg-[url('https://images.unsplash.com/photo-1544367563-12123d8965cd?q=80&w=2070&auto=format&fit=crop')] bg-cover bg-center opacity-5 pointer-events-none" data-id="element-19"></div>
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center relative z-10" data-id="element-20">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="text-center md:text-left" data-id="element-21">
            <span className="inline-block px-4 py-1.5 bg-green-100 text-green-800 rounded-full text-sm font-semibold tracking-wide mb-6" data-id="element-22">
              🌿 BIENESTAR NATURAL
            </span>
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-[#2F4F4F] leading-tight mb-6" data-id="element-23">
              HERBOLARIA ESENCIAL
            </h1>
            <p className="text-xl md:text-2xl text-amber-800 font-medium mb-4" data-id="element-24">
              Una guía simple para saber qué planta usar en cada caso (y cómo
              prepararla)
            </p>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-25">
              Manual práctico con recetas simples para usar plantas en tu día a
              día. Sin conocimientos previos. Sin confusión. Sin leer de más.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start" data-id="element-26">
              <button onClick={scrollToPurchase} className="bg-[#2F4F4F] hover:bg-[#1a2f2f] text-white px-8 py-4 rounded-lg text-lg font-semibold shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2" data-id="element-27">
                Sí, quiero la guía <ArrowRight className="w-5 h-5" data-id="element-28" />
              </button>
            </div>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8
        }} className="relative" data-id="element-29">
            <div className="absolute inset-0 bg-amber-200 rounded-full filter blur-3xl opacity-20 animate-pulse" data-id="element-30"></div>
            <img src={IMAGES.main} alt="Herbolaria Esencial Ebook" className="relative w-full max-w-md mx-auto drop-shadow-2xl cursor-pointer hover:scale-105 transition-transform duration-300" onClick={scrollToPurchase} data-id="element-31" />
          </motion.div>
        </div>
      </header>

      {/* Problem Section */}
      <section className="py-20 px-4 bg-white" data-id="element-32">
        <div className="max-w-3xl mx-auto text-center" data-id="element-33">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-34">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#2F4F4F] mb-8" data-id="element-35">
              Te interesa lo natural… pero te sentís perdido.
            </h2>
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed" data-id="element-36">
              <p data-id="element-37">
                Hay demasiadas recetas sueltas. Demasiados consejos
                contradictorios. Y muy poca claridad.
              </p>
              <p data-id="element-38">Probás una planta. Después otra. Nada se vuelve hábito.</p>
              <p data-id="element-39">
                Saltás de recomendación en recomendación sin seguridad y con la
                sensación de empezar siempre de cero.
              </p>
              <div className="bg-amber-50 p-6 rounded-xl border border-amber-100 my-8" data-id="element-40">
                <p className="font-bold text-amber-900 text-xl" data-id="element-41">
                  👉 No es que no te interese cuidarte. El problema no sos vos.
                </p>
              </div>
              <p data-id="element-42">
                Intentar usar plantas sin una guía clara suele terminar en
                abandono antes de empezar.
              </p>
              <p className="font-medium" data-id="element-43">
                No necesitás más recetas. Necesitás saber qué usar, cuándo y
                cómo, sin tener que leer eternamente.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 px-4 bg-[#2F4F4F] text-white relative overflow-hidden" data-id="element-44">
        <div className="absolute top-0 right-0 w-64 h-64 bg-green-600 rounded-full filter blur-3xl opacity-20" data-id="element-45"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-46">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-47">
            <h2 className="text-3xl md:text-5xl font-serif font-bold mb-6" data-id="element-48">
              Cuando tenés claridad, todo cambia.
            </h2>
            <p className="text-xl text-green-100 mb-12" data-id="element-49">
              Cuidarte deja de ser confuso y se vuelve un hábito simple y
              posible.
            </p>

            <div className="bg-white/10 backdrop-blur-sm p-8 md:p-12 rounded-2xl border border-white/20" data-id="element-50">
              <h3 className="text-2xl md:text-3xl font-serif font-bold text-amber-300 mb-6" data-id="element-51">
                Por eso existe HERBOLARIA ESENCIAL
              </h3>
              <p className="text-lg md:text-xl leading-relaxed mb-6" data-id="element-52">
                Una guía clara para personas que quieren cuidarse con plantas,
                pero se sienten abrumadas por tanta información.
              </p>
              <p className="text-lg md:text-xl leading-relaxed" data-id="element-53">
                En lugar de sumar más recetas, Herbolaria Esencial ordena lo
                importante y te muestra exactamente qué usar en cada situación.
                Con explicaciones simples, lenguaje claro y un enfoque
                responsable para la vida diaria.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 px-4 bg-[#FDFBF7]" data-id="element-54">
        <div className="max-w-6xl mx-auto" data-id="element-55">
          <div className="text-center mb-16" data-id="element-56">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#2F4F4F] mb-4" data-id="element-57">
              📘 ¿Qué vas a recibir?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-58">
            {[{
            icon: <BookOpen className="w-8 h-8 text-green-600" data-id="element-59" />,
            title: 'Guía clara y práctica (PDF)',
            desc: 'Una guía digital con recetas naturales y usos cotidianos de plantas, explicados paso a paso con lenguaje simple y responsable.'
          }, {
            icon: <Leaf className="w-8 h-8 text-green-600" data-id="element-60" />,
            title: 'Contenido ordenado',
            desc: 'Digestión, descanso, energía, piel y bienestar diario, para que sepas qué planta usar según cada caso.'
          }, {
            icon: <Heart className="w-8 h-8 text-green-600" data-id="element-61" />,
            title: 'Pensado para personas reales',
            desc: 'Sin conocimientos previos, sin improvisar y sin información innecesaria.'
          }, {
            icon: <Check className="w-8 h-8 text-green-600" data-id="element-62" />,
            title: 'Todo listo para usar',
            desc: 'Consultás la guía, preparás lo necesario y listo. Sin perder tiempo buscando información suelta.'
          }].map((item, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow" data-id="element-63">
                <div className="bg-green-50 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto" data-id="element-64">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-[#2F4F4F] mb-3 text-center" data-id="element-65">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-center text-sm leading-relaxed" data-id="element-66">
                  {item.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-4 bg-white" data-id="element-67">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12" data-id="element-68">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} data-id="element-69">
            <h3 className="text-2xl font-serif font-bold text-[#2F4F4F] mb-6 flex items-center gap-2" data-id="element-70">
              <span className="text-amber-500" data-id="element-71">✨</span> ¿Qué vas a lograr para
              vos?
            </h3>
            <ul className="space-y-4" data-id="element-72">
              {['Cuidarte con más claridad y confianza.', 'Saber qué planta usar y cómo hacerlo, sin improvisar.', 'Crear hábitos simples que puedas sostener en el tiempo.', 'Sentirte más tranquila y segura al usar plantas en tu día a día.'].map((item, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-73">
                  <Check className="w-5 h-5 text-green-600 shrink-0 mt-1" data-id="element-74" />
                  <span className="text-gray-700" data-id="element-75">{item}</span>
                </li>)}
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} data-id="element-76">
            <h3 className="text-2xl font-serif font-bold text-[#2F4F4F] mb-6 flex items-center gap-2" data-id="element-77">
              <span className="text-amber-500" data-id="element-78">⏳</span> ¿Qué vas a notar a
              corto plazo?
            </h3>
            <ul className="space-y-4" data-id="element-79">
              {['Más claridad desde el inicio.', 'Menos tiempo perdido leyendo guías eternas.', 'Una forma práctica y posible de integrar la herbolaria a tu vida cotidiana.'].map((item, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-80">
                  <Check className="w-5 h-5 text-green-600 shrink-0 mt-1" data-id="element-81" />
                  <span className="text-gray-700" data-id="element-82">{item}</span>
                </li>)}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Bonuses */}
      <section className="py-20 px-4 bg-[#FDFBF7]" data-id="element-83">
        <div className="max-w-5xl mx-auto" data-id="element-84">
          <div className="text-center mb-16" data-id="element-85">
            <span className="inline-block px-4 py-1 bg-red-100 text-red-700 rounded-full text-sm font-bold mb-4" data-id="element-86">
              🎁 REGALOS EXCLUSIVOS
            </span>
            <h2 className="text-3xl md:text-5xl font-serif font-bold text-[#2F4F4F]" data-id="element-87">
              BONOS EXCLUSIVOS — GRATIS HOY
            </h2>
          </div>

          <div className="space-y-16" data-id="element-88">
            {[{
            title: 'BONUS 1 – Botiquín Herbal Esencial',
            desc: 'Guía práctica para saber qué plantas tener siempre a mano y cómo usarlas en situaciones cotidianas.',
            img: IMAGES.bonus1
          }, {
            title: 'BONUS 2 – Fichas Prácticas de Plantas Medicinales',
            desc: 'Fichas de consulta rápida para saber qué usar, cómo prepararlo y qué tener en cuenta, sin releer todo el ebook.',
            img: IMAGES.bonus2
          }, {
            title: 'BONUS 3 – Diario de Uso Herbal Personal',
            desc: 'Un espacio para registrar qué usás, cómo te sentís y qué te funciona mejor con el tiempo.',
            img: IMAGES.bonus3
          }].map((bonus, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className={`flex flex-col ${idx % 2 === 1 ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-8 md:gap-12`} data-id="element-89">
                <div className="flex-1" data-id="element-90">
                  <img src={bonus.img} alt={bonus.title} className="w-full max-w-sm mx-auto rounded-lg shadow-xl hover:scale-105 transition-transform duration-300 cursor-pointer" onClick={scrollToPurchase} data-id="element-91" />
                </div>
                <div className="flex-1 text-center md:text-left" data-id="element-92">
                  <h3 className="text-2xl font-serif font-bold text-[#2F4F4F] mb-4" data-id="element-93">
                    {bonus.title}
                  </h3>
                  <p className="text-lg text-gray-700 mb-6" data-id="element-94">{bonus.desc}</p>
                  <span className="inline-block px-4 py-2 bg-green-100 text-green-800 font-bold rounded-lg" data-id="element-95">
                    GRATIS con tu compra
                  </span>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Pricing / Purchase Section */}
      <section id="purchase-section" className="py-20 px-4 bg-white" data-id="element-96">
        <div className="max-w-4xl mx-auto" data-id="element-97">
          <motion.div initial={{
          scale: 0.95,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} viewport={{
          once: true
        }} className="bg-[#FDFBF7] rounded-3xl shadow-2xl overflow-hidden border-2 border-[#2F4F4F]" data-id="element-98">
            <div className="bg-[#2F4F4F] py-6 text-center text-white" data-id="element-99">
              <h2 className="text-2xl md:text-3xl font-bold" data-id="element-100">
                🎉 OFERTA POR TIEMPO LIMITADO
              </h2>
            </div>

            <div className="p-8 md:p-12 text-center" data-id="element-101">
              <div className="flex flex-col md:flex-row justify-center items-center gap-8 mb-10" data-id="element-102">
                <img src={IMAGES.main} alt="Pack Completo" className="w-48 md:w-64 drop-shadow-xl" data-id="element-103" />
                <div className="text-left space-y-3" data-id="element-104">
                  <div className="flex items-center gap-2" data-id="element-105">
                    <Check className="w-5 h-5 text-green-600" data-id="element-106" />
                    <span data-id="element-107">Ebook Herbolaria Esencial</span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-108">
                    <Check className="w-5 h-5 text-green-600" data-id="element-109" />
                    <span data-id="element-110">Bonus 1: Botiquín Herbal</span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-111">
                    <Check className="w-5 h-5 text-green-600" data-id="element-112" />
                    <span data-id="element-113">Bonus 2: Fichas Prácticas</span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-114">
                    <Check className="w-5 h-5 text-green-600" data-id="element-115" />
                    <span data-id="element-116">Bonus 3: Diario Personal</span>
                  </div>
                </div>
              </div>

              <div className="space-y-2 mb-8" data-id="element-117">
                <p className="text-gray-500 text-lg line-through" data-id="element-118">
                  Antes $49.900 ARS
                </p>
                <p className="text-5xl md:text-6xl font-bold text-[#2F4F4F]" data-id="element-119">
                  HOY $19.990 ARS
                </p>
                <p className="text-sm text-gray-600" data-id="element-120">
                  💳 Hasta 3 cuotas sin interés
                </p>
              </div>

              <a href={CHECKOUT_URL} className="block w-full max-w-lg mx-auto bg-green-600 hover:bg-green-700 text-white text-xl md:text-2xl font-bold py-5 px-8 rounded-xl shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-1 mb-6" data-id="element-121">
                👉 Sí, quiero acceder a Herbolaria Esencial ahora
              </a>

              <div className="flex flex-col md:flex-row justify-center gap-4 text-sm text-gray-600" data-id="element-122">
                <span className="flex items-center justify-center gap-1" data-id="element-123">
                  <ShieldCheck className="w-4 h-4" data-id="element-124" /> Compra Segura
                </span>
                <span className="flex items-center justify-center gap-1" data-id="element-125">
                  📩 Entrega inmediata en PDF
                </span>
                <span className="flex items-center justify-center gap-1" data-id="element-126">
                  📌 Descarga para siempre
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-[#FDFBF7]" data-id="element-127">
        <div className="max-w-6xl mx-auto" data-id="element-128">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-center text-[#2F4F4F] mb-12" data-id="element-129">
            Lo que dicen quienes ya lo leyeron
          </h2>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-130">
            {TESTIMONIALS.map((t, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-131">
                <div className="flex gap-1 mb-4" data-id="element-132">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-amber-400 fill-current" data-id="element-133" />)}
                </div>
                <p className="text-gray-700 italic mb-6" data-id="element-134">"{t.text}"</p>
                <div className="flex items-center gap-3" data-id="element-135">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-700 font-bold" data-id="element-136">
                    {t.name.charAt(0)}
                  </div>
                  <div data-id="element-137">
                    <p className="font-bold text-sm text-gray-900" data-id="element-138">{t.name}</p>
                    <p className="text-xs text-gray-500" data-id="element-139">
                      {t.age} años, {t.city}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2F4F4F] text-white py-12 px-4 text-center" data-id="element-140">
        <div className="max-w-4xl mx-auto" data-id="element-141">
          <h3 className="text-2xl font-serif font-bold mb-4" data-id="element-142">
            HERBOLARIA ESENCIAL
          </h3>
          <p className="text-green-200 mb-8" data-id="element-143">
            Tu guía para el bienestar natural diario.
          </p>
          <p className="text-sm text-green-400" data-id="element-144">
            © {new Date().getFullYear()} Herbolaria Esencial. Todos los
            derechos reservados.
            <br data-id="element-145" />
            Este producto no sustituye el consejo médico profesional.
          </p>
        </div>
      </footer>
    </div>;
}